import { CRM_COPY } from './copy';
import { failureLabel, LEAD_FIELD_LABELS } from '../../leadRules/labels';
import { LEAD_RULES_COPY } from '../../leadRules/copy';
import type { LeadCompleteness } from '@viq-admin/types';
import type { CommitOutcome, CommitResultRow, MergePolicy, PersonPreview } from './types';
import type { CrmQueueState } from './useCrmQueue';

/**
 * What the Send step says about a selection, shared by the legacy `SendView`
 * and the unified grid's Send lens so the two can never word the same send
 * differently. Moved here verbatim from `SendView` (port plan step 6); its
 * behaviour is unchanged and still pinned by `SendView.test.tsx`.
 */

/** Outcome Tag colours, keyed by `CommitResultRow.outcome`. */
export const OUTCOME_TAG_COLOR: Partial<Record<CommitResultRow['outcome'], string>> = {
  created: 'green',
  updated: 'blue',
  changed_since_preview: 'orange',
  failed: 'red',
  // Gold, not red: a lead the rules held back is not a lead the CRM rejected
  // — same amber the ready-rows list above already uses for the same
  // completeness signal. Red is reserved for `failed`, an actual error.
  incomplete: 'gold',
};

/** The outcomes where the lead itself landed in the CRM. */
const WENT_THROUGH = new Set<CommitResultRow['outcome']>(['created', 'updated', 'membership_only']);

/**
 * Whether every lead in a finished send landed, which is when the send says
 * "Sent to your CRM." and not the partial toast: no row failed, was held back
 * by lead rules, changed in the CRM since the look-up, or matched several CRM
 * records. Reads the summary and the rows both, so a summary from an older
 * backend that lacks a count still can't claim a clean send.
 */
export function everyLeadWentThrough(outcome: CommitOutcome): boolean {
  const s = outcome.summary;
  return (
    s.failed === 0 &&
    !s.incomplete &&
    !s.changedSincePreview &&
    !s.ambiguous &&
    outcome.results.every((r) => WENT_THROUGH.has(r.outcome))
  );
}

/**
 * Why the company's lead rules hold a lead back: the notes count against the
 * minimum and the held fields, e.g. "Notes 312/500 · Missing: Phone".
 */
export function heldReasonText(completeness: LeadCompleteness): string {
  const parts: string[] = [];
  const notes = completeness.failures.find((f) => f.type === 'notes_min_chars');
  if (notes) parts.push(failureLabel(notes));
  // Spec 2026-09-28 §6: the fields the company's rules hold the lead for.
  const held = completeness.held ?? [];
  if (held.length > 0) parts.push(`${LEAD_RULES_COPY.heldPrefix} ${held.map((f) => LEAD_FIELD_LABELS[f]).join(', ')}`);
  return parts.length > 0 ? parts.join(' · ') : LEAD_RULES_COPY.incompleteTag;
}

/** Why a previewed person isn't ready to send (`isReady` is false). */
export function unreadyReason(p: PersonPreview): string {
  // Company lead rules first: a notes-short or held-back lead in `block` mode
  // is held back whatever its bucket; the parts below say why.
  if (p.completeness?.sendBlocked) return heldReasonText(p.completeness);
  if (p.bucket === 'needs_email') return CRM_COPY.reasonNoEmailYet;
  // The only other bucket `isReady` rules out.
  return CRM_COPY.reasonMultipleCrmRecords;
}

/**
 * Builds the one-sentence summary from the current selection. Segments with
 * a zero count are omitted entirely (p3 Task 5 ruling) rather than rendering
 * "0 …" clauses.
 */
export function buildSummary(counts: {
  nCreate: number;
  nFill: number;
  nOverwrite: number;
  nChoices: number;
  nSuggested: number;
}): string {
  const segments: string[] = [];
  if (counts.nCreate > 0) segments.push(`${counts.nCreate} ${CRM_COPY.summaryCreateSuffix}`);
  if (counts.nFill > 0) segments.push(`${counts.nFill} ${CRM_COPY.summaryFillSuffix}`);
  if (counts.nOverwrite > 0) segments.push(`${counts.nOverwrite} ${CRM_COPY.summaryOverwriteSuffix}`);
  if (counts.nChoices > 0) segments.push(`${counts.nChoices} ${CRM_COPY.summaryChoicesSuffix}`);
  if (counts.nSuggested > 0) segments.push(`${counts.nSuggested} ${CRM_COPY.summarySuggestedSuffix}`);
  return segments.length > 0 ? `${segments.join('; ')}.` : '';
}

/** Reasons whose commit-time outcome flips with the merge policy — the ones
 *  an `overwrite` run silently replaces unless the operator picked a side. */
const CONFLICT_REASONS = new Set(['conflict_default', 'policy_overwrite']);

/** The summary sentence for the people about to be sent. */
export function selectionSummaryText(
  selectedPreviews: PersonPreview[],
  state: Pick<CrmQueueState, 'fieldChoices' | 'ownerPicks'>,
  effectivePolicy: MergePolicy,
): string {
  let nCreate = 0;
  let nFill = 0;
  let nOverwrite = 0;
  let nChoices = 0;
  let nSuggested = 0;
  for (const p of selectedPreviews) {
    const fieldChoices = state.fieldChoices[p.personId];
    if (p.bucket === 'new') nCreate += 1;
    if (p.bucket === 'existing' && p.decisions.some((d) => d.action === 'fill')) nFill += 1;
    // Under an overwrite run, every conflict the operator did NOT pick a
    // side for gets the CRM's value replaced — say so before they send.
    if (
      effectivePolicy === 'overwrite' &&
      p.bucket === 'existing' &&
      p.decisions.some((d) => CONFLICT_REASONS.has(d.reason) && !fieldChoices?.[d.field])
    ) {
      nOverwrite += 1;
    }
    if (fieldChoices && Object.keys(fieldChoices).length > 0) nChoices += 1;
    // Only owner-required rows ever carry the suggestion onto the wire
    // (commitBodyFrom's §9 gate) — the count must match what's sent.
    if (p.ownerRequired && !state.ownerPicks[p.personId] && p.suggestedOwnerId) nSuggested += 1;
  }
  return buildSummary({ nCreate, nFill, nOverwrite, nChoices, nSuggested });
}
